import { onMounted, onUnmounted } from 'vue';

/**
 * Composable to handle dynamic viewport height for mobile browsers.
 *
 * This fixes the issue where iOS Safari/Chrome address bar causes blank space
 * at the bottom when scrolling up. It sets a CSS custom property `--vh` that
 * represents 1% of the actual visual viewport height.
 *
 * Usage:
 * - Call this composable once in App.vue to make --vh globally available
 * - Use in CSS: height: calc(var(--vh, 1vh) * 100)
 * - Or use Tailwind utility: h-screen-dynamic or min-h-screen-dynamic
 */
export function useDynamicVh() {
  const setVh = () => {
    // Get actual visual viewport height
    const vh = window.innerHeight * 0.01;
    // Set CSS custom property on document root
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };

  onMounted(() => {
    // Set initial value
    setVh();

    // Update on window resize (orientation change, keyboard show/hide)
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);
  });

  onUnmounted(() => {
    // Clean up event listeners
    window.removeEventListener('resize', setVh);
    window.removeEventListener('orientationchange', setVh);
  });
}
